/**
 * Sprint 3a — 6 Mrt 2026
 * PublicBookingItemsEditor — publieke variant van de items-editor voor het
 * boekingsformulier op de website (BoekingsformulierPage.jsx).
 *
 * Visueel: klant-vriendelijke kaart-per-item met rounded-2xl, brand-kleuren
 * en ruimtes, past bij de bestaande 5999Cargo huisstijl (i.p.v. de scherpe
 * backoffice-stijl van BookingItemsEditor.jsx).
 *
 * Bevat dezelfde CBM/validatie-logica (deelt helpers uit de backoffice-editor).
 *
 * Props:
 *   items                — array of BookingFormItem
 *   onChange(items)      — parent state update
 *   currency             — factuur-valuta (weergave)
 *   wantsValueInvoice    — bool. Als true → value_per_unit verplicht + extra velden
 *   labels               — { addItem, duplicate, remove, ... } — i18n-strings vanuit parent
 */
import React, { useMemo, useCallback } from "react";
import { Plus, Copy, Trash2, Package, AlertTriangle } from "lucide-react";
import {
  computeLineCbm, computeTotals, missingRequiredFields,
} from "../admin/sections/BookingItemsEditor";

const UNITS = [
  { value: "cm", label: "cm" },
  { value: "meter", label: "meter" },
  { value: "inch", label: "inch" },
];

const EMPTY_ITEM = {
  description: "",
  quantity: 1,
  length: "",
  width: "",
  height: "",
  unit: "cm",
  weight: "",
  value_per_unit: "",
  condition: "",
  hs_code: "",
  country_of_origin: "",
  note: "",
};

function fmtCbm(n) { return Number(n || 0).toFixed(4); }
function fmtCurrency(n, cur = "EUR") {
  return Number(n || 0).toLocaleString("nl-NL", { style: "currency", currency: cur });
}

export default function PublicBookingItemsEditor({
  items = [],
  onChange,
  currency = "EUR",
  wantsValueInvoice = false,
}) {
  const totals = useMemo(() => computeTotals(items), [items]);

  const setItem = useCallback((idx, next) => {
    onChange(items.map((it, i) => (i === idx ? next : it)));
  }, [items, onChange]);

  const addItem = useCallback(() => {
    onChange([...items, { ...EMPTY_ITEM }]);
  }, [items, onChange]);

  const duplicateItem = useCallback((idx) => {
    const source = items[idx];
    const clone = { ...source };
    const next = [...items];
    next.splice(idx + 1, 0, clone);
    onChange(next);
  }, [items, onChange]);

  const removeItem = useCallback((idx) => {
    onChange(items.filter((_, i) => i !== idx));
  }, [items, onChange]);

  return (
    <div className="space-y-4" data-testid="public-booking-items-editor">
      {(items || []).length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-brand-200 bg-brand-50/40 p-8 text-center">
          <Package className="w-8 h-8 text-brand-500 mx-auto mb-2" />
          <p className="text-sm text-ink-700 mb-3">
            Voeg uw eerste artikel toe om te beginnen. U kunt meerdere artikelen achter elkaar toevoegen.
          </p>
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold px-5 py-2.5 transition-colors"
            data-testid="public-items-add-first"
          >
            <Plus className="w-4 h-4" />
            Eerste artikel toevoegen
          </button>
        </div>
      )}

      {(items || []).map((it, idx) => {
        const missing = missingRequiredFields(it, wantsValueInvoice);
        const lineCbm = computeLineCbm(it);
        const lineValue = Number(it.value_per_unit || 0) * Number(it.quantity || 0);
        const isInvalid = missing.length > 0;

        return (
          <div
            key={idx}
            className={`rounded-2xl border ${
              isInvalid ? "border-red-200 bg-red-50/30" : "border-gray-200 bg-white"
            } p-4 sm:p-5 shadow-sm transition-colors`}
            data-testid={`public-booking-item-${idx}`}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                  isInvalid ? "bg-red-100 text-red-800" : "bg-brand-100 text-brand-800"
                }`}>{idx + 1}</span>
                <h4 className="font-chivo font-bold text-base text-ink-900">
                  Artikel {idx + 1}
                </h4>
              </div>
              <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => duplicateItem(idx)}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-xs px-2.5 py-1.5 text-ink-700"
                        data-testid={`public-booking-item-${idx}-duplicate`}
                        aria-label={`Dupliceer artikel ${idx + 1}`}>
                  <Copy className="w-3.5 h-3.5" /> Dupliceer
                </button>
                <button type="button" onClick={() => removeItem(idx)}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-100 hover:border-red-300 hover:bg-red-50 text-xs px-2.5 py-1.5 text-red-700"
                        data-testid={`public-booking-item-${idx}-remove`}
                        aria-label={`Verwijder artikel ${idx + 1}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Melding onvolledig */}
            {isInvalid && (
              <div className="mb-3 flex items-start gap-2 rounded-xl bg-red-100/60 border border-red-200 px-3 py-2 text-xs text-red-800"
                   data-testid={`public-booking-item-${idx}-missing`}>
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Vul deze regel volledig in of verwijder de regel. Ontbrekend: <strong>{missing.join(", ")}</strong>.</span>
              </div>
            )}

            {/* Omschrijving */}
            <div className="mb-3">
              <label className="block text-xs font-semibold text-ink-700 mb-1">
                Omschrijving <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={it.description || ""}
                onChange={(e) => setItem(idx, { ...it, description: e.target.value })}
                placeholder="Bijv. Doos met kleding, Barrel, Meubels, TV, Pallet…"
                className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                data-testid={`public-booking-item-${idx}-description`}
              />
            </div>

            {/* Aantal + L/B/H + eenheid grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-2">
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Aantal <span className="text-red-600">*</span>
                </label>
                <input type="number" min="1" step="1" inputMode="numeric"
                       value={it.quantity ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, quantity: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-quantity`} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Lengte <span className="text-red-600">*</span>
                </label>
                <input type="number" min="0" step="0.01" inputMode="decimal"
                       value={it.length ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, length: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-length`} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Breedte <span className="text-red-600">*</span>
                </label>
                <input type="number" min="0" step="0.01" inputMode="decimal"
                       value={it.width ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, width: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-width`} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Hoogte <span className="text-red-600">*</span>
                </label>
                <input type="number" min="0" step="0.01" inputMode="decimal"
                       value={it.height ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, height: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-height`} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Eenheid <span className="text-red-600">*</span>
                </label>
                <select value={it.unit || "cm"}
                        onChange={(e) => setItem(idx, { ...it, unit: e.target.value })}
                        className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm bg-white h-10"
                        data-testid={`public-booking-item-${idx}-unit`}>
                  {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </div>
            </div>

            {/* Gewicht (altijd optioneel) + waardefactuur velden (voorwaardelijk) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Gewicht (kg)
                </label>
                <input type="number" min="0" step="0.1" inputMode="decimal"
                       value={it.weight ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, weight: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       placeholder="Optioneel"
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-weight`} />
              </div>
              {wantsValueInvoice && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-ink-700 mb-1">
                      Waarde per stuk ({currency}) <span className="text-red-600">*</span>
                    </label>
                    <input type="number" min="0" step="0.01" inputMode="decimal"
                           value={it.value_per_unit ?? ""}
                           onChange={(e) => setItem(idx, {
                             ...it, value_per_unit: e.target.value === "" ? "" : Number(e.target.value),
                           })}
                           className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                           data-testid={`public-booking-item-${idx}-value-per-unit`} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-ink-700 mb-1">Staat</label>
                    <select value={it.condition || ""}
                            onChange={(e) => setItem(idx, { ...it, condition: e.target.value })}
                            className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm bg-white h-10"
                            data-testid={`public-booking-item-${idx}-condition`}>
                      <option value="">— optioneel —</option>
                      <option value="nieuw">Nieuw</option>
                      <option value="gebruikt">Gebruikt</option>
                      <option value="persoonlijk_bezit">Persoonlijk bezit</option>
                      <option value="commercieel">Commercieel</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-ink-700 mb-1">
                      Land van oorsprong
                    </label>
                    <input type="text"
                           value={it.country_of_origin || ""}
                           onChange={(e) => setItem(idx, { ...it, country_of_origin: e.target.value })}
                           placeholder="Optioneel"
                           className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                           data-testid={`public-booking-item-${idx}-origin`} />
                  </div>
                  <div className="col-span-2 sm:col-span-4">
                    <label className="block text-xs font-semibold text-ink-700 mb-1">
                      HS-code (optioneel)
                    </label>
                    <input type="text"
                           value={it.hs_code || ""}
                           onChange={(e) => setItem(idx, { ...it, hs_code: e.target.value })}
                           placeholder="Bijv. 6203.42 — laat leeg als u dit niet weet"
                           className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                           data-testid={`public-booking-item-${idx}-hs-code`} />
                  </div>
                </>
              )}
              <div className={wantsValueInvoice ? "col-span-2 sm:col-span-4" : "col-span-2 sm:col-span-3"}>
                <label className="block text-xs font-semibold text-ink-700 mb-1">
                  Notitie
                </label>
                <input type="text"
                       value={it.note || ""}
                       onChange={(e) => setItem(idx, { ...it, note: e.target.value })}
                       placeholder="Optioneel — bijv. fragile, verzekerd"
                       className="w-full rounded-xl border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 px-3 py-2 text-sm"
                       data-testid={`public-booking-item-${idx}-note`} />
              </div>
            </div>

            {/* Regeltotaal */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-ink-600">
              <span>Regel CBM: <strong className="font-mono text-ink-900" data-testid={`public-booking-item-${idx}-cbm`}>
                {fmtCbm(lineCbm)}
              </strong></span>
              {Number(it.weight) > 0 && Number(it.quantity) > 0 && (
                <span>Gewicht totaal: <strong className="font-mono">{(Number(it.weight) * Number(it.quantity)).toFixed(1)} kg</strong></span>
              )}
              {wantsValueInvoice && lineValue > 0 && (
                <span>Regelwaarde: <strong className="font-mono text-brand-800"
                                          data-testid={`public-booking-item-${idx}-line-value`}>
                  {fmtCurrency(lineValue, currency)}
                </strong></span>
              )}
            </div>
          </div>
        );
      })}

      {(items || []).length > 0 && (
        <button type="button" onClick={addItem}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-300 hover:border-brand-500 hover:bg-brand-50/40 text-brand-700 font-semibold text-sm py-3 transition-colors"
                data-testid="public-items-add">
          <Plus className="w-4 h-4" />
          Nog een artikel toevoegen
        </button>
      )}

      {/* Live totalen */}
      {(items || []).length > 0 && (
        <div className="rounded-2xl bg-gradient-to-r from-brand-800 to-brand-700 text-white p-4 sm:p-5"
             data-testid="public-items-totals">
          <div className="flex flex-wrap gap-x-8 gap-y-3 items-center">
            <div>
              <div className="text-xs uppercase tracking-wide text-white/70">Totaal aantal</div>
              <div className="font-chivo font-bold text-2xl" data-testid="public-totals-pieces">
                {Math.round(totals.pieces)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-white/70">Totaal CBM</div>
              <div className="font-chivo font-bold text-2xl font-mono" data-testid="public-totals-cbm">
                {fmtCbm(totals.cbm)}
              </div>
            </div>
            {totals.weight > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-white/70">Totaal gewicht</div>
                <div className="font-chivo font-bold text-2xl font-mono" data-testid="public-totals-weight">
                  {totals.weight.toFixed(1)} kg
                </div>
              </div>
            )}
            {wantsValueInvoice && totals.value > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-white/70">Totale waarde</div>
                <div className="font-chivo font-bold text-2xl" data-testid="public-totals-value">
                  {fmtCurrency(totals.value, currency)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
